/**
 * Today Dashboard - Primary Home Screen for Therappi®
 * 
 * Premium wellness app experience inspired by Headspace/Calm aesthetics.
 * Calm, intelligent overview of what matters most today.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useTodayDashboardData } from '@/hooks/useTodayDashboardData';
import { AppHeader } from '@/components/AppHeader';
import { UnifiedScoreCard } from '@/components/dashboard/today/UnifiedScoreCard';
import { TodayInsightCard } from '@/components/dashboard/today/TodayInsightCard';
import { NextActionsGrid } from '@/components/dashboard/today/NextActionsGrid';
import { TimelineStrip } from '@/components/dashboard/today/TimelineStrip';
import { QuickAccessGrid } from '@/components/dashboard/today/QuickAccessGrid';
import { FutureSelfSpotlight } from '@/components/dashboard/today/FutureSelfSpotlight';
import { RitualsDashboardCard } from '@/components/rituals/RitualsDashboardCard';
import { BreathworkHeroCard, DailyStreakWidget, FeatureSpotlight } from '@/components/dashboard/today/FeatureSpotlight';
import { QuickActionsStrip } from '@/components/dashboard/today/QuickActionsStrip';
import { WhoopDashboardCard } from '@/components/dashboard/WhoopDashboardCard';
import { AITierIndicator } from '@/components/AITierIndicator';
import { InsightOfTheDayWidget } from '@/components/insights/InsightOfTheDayWidget';
import { WelcomeBackSummary } from '@/components/dashboard/WelcomeBackSummary';
import { Sparkles, Sun, Moon, Sunrise, CloudSun } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';

const getTimeOfDayIcon = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return <Sunrise className="w-8 h-8 text-secondary" />;
  if (hour >= 12 && hour < 17) return <Sun className="w-8 h-8 text-secondary" />;
  if (hour >= 17 && hour < 21) return <CloudSun className="w-8 h-8 text-secondary" />;
  return <Moon className="w-8 h-8 text-secondary" />;
};

export const TodayDashboard: React.FC = () => {
  const { loading, error, data } = useTodayDashboardData();
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    if (user) {
      await supabase.rpc('log_therapy_action', {
        p_user_id: user.id,
        p_action: 'user_logout',
        p_details: { timestamp: new Date().toISOString() }
      });
    }
    await supabase.auth.signOut();
  };

  // NOTE: OnboardingWrapper handles auth gating via RULE 0
  // If we reach here without a user, show minimal loading (should be rare)
  // The "Preparing your space..." message is intentionally simple - OnboardingWrapper
  // shows the main loading spinner during auth resolution
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader user={user} onSignOut={handleSignOut} />
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-accent/10 animate-pulse" />
                <Sparkles className="w-8 h-8 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
              </div>
              <div className="text-center space-y-2">
                <p className="text-lg text-foreground/80 font-light">Gathering your insights...</p>
                <p className="text-sm text-muted-foreground">Taking a moment to understand your day</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader user={user} onSignOut={handleSignOut} />
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 text-center">
              <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <p className="text-lg text-foreground/80">Something didn't quite work</p>
                <p className="text-sm text-muted-foreground">Please refresh to try again</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onSignOut={handleSignOut} />

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Premium Greeting Section */}
          <section className="mb-10 sm:mb-14 animate-fade-in">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="mb-2 opacity-80">
                {getTimeOfDayIcon()}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extralight text-foreground tracking-wide">
                {data.greeting}, <span className="text-primary">{data.displayName}</span>
              </h1>
              {data.topInsight && (
                <p className="text-base sm:text-lg text-muted-foreground font-light max-w-xl leading-relaxed">
                  {data.topInsight.title}
                </p>
              )}
            </div>
          </section>

          {/* Main Content with Staggered Animation */}
          <div className="space-y-8 sm:space-y-10">
            {/* Quick Actions Strip - instant access to top 6 features */}
            <QuickActionsStrip />

            {/* Welcome Back Summary - Shows yesterday's activity */}
            {user && <WelcomeBackSummary userId={user.id} />}

            {/* Daily Healthy Habits - Morning & Evening Routines */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.02 }}
            >
              <RitualsDashboardCard />
            </motion.section>

            {/* WHOOP Integration Card - Prominent placement */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 }}
            >
              <WhoopDashboardCard />
            </motion.section>

            {/* Breathwork Hero Card - New! */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
            >
              <BreathworkHeroCard />
            </motion.section>

            {/* Daily Streak Widget */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <DailyStreakWidget streak={data.unifiedScore.value > 50 ? 3 : 0} />
            </motion.section>

            {/* Unified Score Card */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <UnifiedScoreCard
                value={data.unifiedScore.value}
                statusLabel={data.unifiedScore.statusLabel}
                mentalScore={data.unifiedScore.mentalScore}
                physicalScore={data.unifiedScore.physicalScore}
                recoveryScore={data.unifiedScore.recoveryScore}
              />
            </motion.section>

            {/* AI Insights Widget (Premium Only) */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <InsightOfTheDayWidget />
            </motion.section>

            {/* Feature Spotlight - Quick Access */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <FeatureSpotlight />
            </motion.section>

            {/* AI Tier Indicator */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <AITierIndicator variant="card" />
            </motion.section>

            {/* Today's Insight */}
            {data.topInsight && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
              >
                <TodayInsightCard
                  title={data.topInsight.title}
                  body={data.topInsight.body}
                  levelTags={data.topInsight.levelTags}
                />
              </motion.section>
            )}

            {/* Next Actions */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <NextActionsGrid actions={data.recommendedActions} />
            </motion.section>

            {/* Timeline Strip */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
            >
              <TimelineStrip
                hasRecentSanctuaryEntry={data.timelineSummary.hasRecentSanctuaryEntry}
                pastNote={data.timelineSummary.pastNote}
                presentNote={data.timelineSummary.presentNote}
                futureNote={data.timelineSummary.futureNote}
              />
            </motion.section>

            {/* Future Self Spotlight */}
            {data.futureSelf.active && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <FutureSelfSpotlight
                  identityStatement={data.futureSelf.identityStatement}
                  currentPivotalStep={data.futureSelf.currentPivotalStep}
                />
              </motion.section>
            )}

            {/* Quick Access Grid */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
            >
              <QuickAccessGrid links={data.quickLinks} />
            </motion.section>
          </div>

          {/* Disclaimer */}
          <footer className="mt-16 sm:mt-20 pb-8 text-center animate-fade-in" style={{ animationDelay: '500ms' }}>
            <div className="max-w-md mx-auto">
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-border to-transparent mx-auto mb-6" />
              <p className="text-xs text-muted-foreground leading-relaxed font-light">
                Therappi<sup className="text-[0.6em]">®</sup> is designed to support your wellness journey.
                It is not a replacement for professional therapeutic support or medical advice.
              </p>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default TodayDashboard;